import { useQuery } from 'react-query';
import {
  AddRentLineDto,
  CreateRentDto,
  RemoveEquipmentDto,
  RentResponse,
  UpdateEquipmentPaymentValue,
  UpdateRentCustomerOrAddressDto,
  UpdateRentDateDto,
} from '../../../api/client/data-contracts';
import { Rents } from '../../../api/client/Rents';
import { QueriedData, showErrorNotification } from '../assets/useAssets';
import { useNotifiedMutation } from '../useNotifiedMutation';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { defaultApiConfig } from '../../../api';

dayjs.extend(isBetween);

type RentCommandArg<T> = { rentId: string; dto: T };
type AddRentEquipmentArg = { rentId: string; dto: AddRentLineDto };
type RemoveEquipmentArg = { rentId: string; dto: RemoveEquipmentDto };
type CreateRentArg = {
  dto: CreateRentDto;
  // Predefined equipment to pre-fill on the newly created rent.
  equipments?: AddRentLineDto[];
};

export interface UseRentsResult {
  rentsQuery: QueriedData<RentResponse[]>;
  createRent: (arg: CreateRentArg) => void;
  startRent: (rentId: string) => void;
  reserveRent: (rentId: string) => void;
  cancelRent: (rentId: string) => void;
  endRent: (rentId: string) => void;
  markRentAsPayed: (rentId: string) => void;
  markRentAsUnpayed: (rentId: string) => void;
  addRentEquipment: (data: AddRentEquipmentArg) => void;
  removeEquipment: (data: RemoveEquipmentArg) => void;
  updateRentDate: (data: RentCommandArg<UpdateRentDateDto>) => void;
  updateRentCustomerOrAddress: (
    data: RentCommandArg<UpdateRentCustomerOrAddressDto>,
  ) => void;
  openRent: (rentId: string) => void;
  updateRentEquipmentPaymentValue: (
    data: RentCommandArg<UpdateEquipmentPaymentValue>,
  ) => void;

  bulkDeleteRents: (ids: string[]) => void;
}

const rentsApi = new Rents(defaultApiConfig);
export interface ReadRentsQuery {
  state?: RentResponse['state'];
  startDateInterval?: { begin: string; end: string };
  asset?: string;
  model?: string;
  brand?: string;
  tags?: string[];
}

export function useRents(query?: ReadRentsQuery): UseRentsResult {
  const queryKey = ['rents', JSON.stringify(query)];

  let queryParams = query;
  if (!query || Object.entries(query).length === 0) {
    queryParams = undefined;
  }

  const rentsQuery = useQuery(queryKey, () =>
    rentsApi
      .findRents(queryParams || {})
      .then((res) => res.data.filter((r) => r.state !== 'DELETED')),
  );

  const intl = useIntl();
  const history = useHistory();

  const { mutate: createRent } = useNotifiedMutation(
    async ({ dto, equipments }: CreateRentArg) => {
      const created = await rentsApi.createRent(dto).then((res) => res.data);
      const rentId = (created as any).id as string;

      // Pre-fill the customer's predefined equipment. Equipment that cannot be
      // added (e.g. currently rented) is skipped so scheduling never blocks.
      let skipped = 0;
      if (rentId && equipments && equipments.length > 0) {
        for (const equipment of equipments) {
          try {
            await rentsApi.addEquipment(rentId, {
              asset: equipment.asset,
              paymentValue: equipment.paymentValue,
            });
          } catch {
            skipped += 1;
          }
        }
      }

      return { rentId, skipped };
    },
    {
      successId: 'rent-create-success-message',
      errorId: 'rent-create-fail-message',
      invalidate: queryKey,
      onSuccess: (result) => {
        if (result.skipped > 0) {
          showErrorNotification(
            'rent-prefill-equipments-skipped-message',
            intl,
          );
        }
        history.push(`/apps/cockpit/rents/${result.rentId}`);
      },
    },
  );

  const { mutate: startRent } = useNotifiedMutation(
    (rentId: string) => rentsApi.startRent(rentId).then((res) => res.data),
    {
      successId: 'rent-start-success-message',
      errorId: 'rent-start-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: reserveRent } = useNotifiedMutation(
    (rentId: string) => rentsApi.reserveRent(rentId).then((res) => res.data),
    {
      successId: 'rent-reserve-success-message',
      errorId: 'rent-reserve-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: endRent } = useNotifiedMutation(
    (rentId: string) => rentsApi.endRent(rentId).then((res) => res.data),
    {
      successId: 'rent-end-success-message',
      errorId: 'rent-end-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: cancelRent } = useNotifiedMutation(
    (rentId: string) => rentsApi.cancelRent(rentId).then((res) => res.data),
    {
      successId: 'rent-cancel-success-message',
      errorId: 'rent-cancel-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: bulkDeleteRents } = useNotifiedMutation(
    (rentIds: string[]) =>
      rentsApi.bulkDeleteRent({ ids: rentIds }).then((res) => res.data),
    {
      successId: 'rent-bulk-delete-success-message',
      errorId: 'rent-bulk-delete-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: markRentAsPayed } = useNotifiedMutation(
    (rentId: string) => rentsApi.payRent(rentId).then((res) => res.data),
    {
      successId: 'rent-mark-payed-success-message',
      errorId: 'rent-mark-payed-error-message',
      invalidate: queryKey,
    },
  );

  const { mutate: markRentAsUnpayed } = useNotifiedMutation(
    (rentId: string) => rentsApi.unpayRent(rentId).then((res) => res.data),
    {
      successId: 'rent-mark-not-payed-success-message',
      errorId: 'rent-mark-not-payed-error-message',
      invalidate: queryKey,
    },
  );

  const { mutate: addRentEquipment } = useNotifiedMutation(
    (data: AddRentEquipmentArg) =>
      rentsApi.addEquipment(data.rentId, data.dto).then((res) => res.data),
    {
      successId: 'rent-add-equipment-success-message',
      errorId: 'rent-add-equipment-error-message',
      invalidate: queryKey,
    },
  );

  const { mutate: removeEquipment } = useNotifiedMutation(
    (data: RemoveEquipmentArg) =>
      rentsApi.removeEquipment(data.rentId, data.dto).then((res) => res.data),
    {
      successId: 'rent-remove-equipment-success-message',
      errorId: 'rent-remove-equipment-error-message',
      invalidate: queryKey,
    },
  );

  const { mutate: updateRentEquipmentPaymentValue } = useNotifiedMutation(
    (data: RentCommandArg<UpdateEquipmentPaymentValue>) =>
      rentsApi
        .updateEquipmentPaymentValue(data.rentId, data.dto)
        .then((res) => res.data),
    {
      successId: 'rent-equipment-payment-value-updated-success-message',
      errorId: 'rent-equipment-payment-value-updated-error-message',
      invalidate: queryKey,
    },
  );

  const { mutate: updateRentDate } = useNotifiedMutation(
    (data: RentCommandArg<UpdateRentDateDto>) =>
      rentsApi.updateRentDate(data.rentId, data.dto).then((res) => res.data),
    {
      successId: 'rent-update-date-success-message',
      errorId: 'rent-update-date-error-message',
      invalidate: queryKey,
    },
  );

  const { mutate: updateRentCustomerOrAddress } = useNotifiedMutation(
    (data: RentCommandArg<UpdateRentCustomerOrAddressDto>) =>
      rentsApi
        .updateRentCustomerOrAddress(data.rentId, data.dto)
        .then((res) => res.data),
    {
      successId: 'rent-update-success-message',
      errorId: 'rent-update-error-message',
      invalidate: queryKey,
    },
  );

  function openRent(rentId: string) {
    history.push(`/apps/cockpit/rents/${rentId}`);
  }

  return {
    rentsQuery,
    createRent,
    startRent,
    endRent,
    reserveRent,
    cancelRent,
    markRentAsPayed,
    markRentAsUnpayed,
    addRentEquipment,
    openRent,
    removeEquipment,
    updateRentDate,
    updateRentCustomerOrAddress,
    updateRentEquipmentPaymentValue,
    bulkDeleteRents,
  };
}

export interface UseAssetAvailabilityResult {
  isAvailable(date: Dayjs): boolean;
  isAvailableEndDate(date: Dayjs, startDate: Dayjs): boolean;
  isAvailableStartDate(date: Dayjs, startDate: Dayjs): boolean;

  ready: boolean;
}

export function useAssetAvailability(
  currentRentId: string,
  serialNumbers: number[],
): UseAssetAvailabilityResult {
  const { rentsQuery } = useRents();

  function isAvailable(date: Dayjs) {
    if (!rentsQuery.data) {
      return false;
    }

    const assetRents = rentsQuery.data
      .filter((rent) => rent.id !== currentRentId)
      .filter(
        (rent) =>
          !!rent.rentLines.find((line) =>
            serialNumbers.includes(Number(line.asset.serialNumber)),
          ),
      );
    for (const rent of assetRents) {
      if (
        date.isBetween(rent.startDate, rent.endDate, 'days') ||
        date.isSame(rent.startDate, 'days') ||
        date.isSame(rent.endDate, 'days')
      ) {
        return false;
      }
    }

    return true;
  }

  function isAvailableEndDate(current: Dayjs, selectedStartDate: Dayjs) {
    if (!rentsQuery.data) {
      return false;
    }
    const assetRents = rentsQuery.data
      .filter((rent) => rent.id !== currentRentId)
      .filter(
        (rent) =>
          !!rent.rentLines.find((line) =>
            serialNumbers.includes(Number(line.asset.serialNumber)),
          ),
      );

    // The rents come from the api sorted ascendently by startDate
    const startRentDates: string[] = assetRents
      .sort((rentA, rentB) => {
        if (dayjs(rentA.startDate).isAfter(rentB.startDate)) {
          return 1;
        }

        if (dayjs(rentA.startDate).isBefore(rentB.startDate)) {
          return -1;
        }

        return 0;
      })
      .map((rent) => rent.startDate);

    const closestNextStartDay = startRentDates.find((date) =>
      selectedStartDate.isBefore(date),
    );

    if (!closestNextStartDay) {
      return true;
    }
    return current.isBefore(closestNextStartDay, 'days');
  }

  function isAvailableStartDate(current: Dayjs, selectedEndDate: Dayjs) {
    if (!rentsQuery.data) {
      return false;
    }
    const assetRents = rentsQuery.data
      .filter((rent) => rent.id !== currentRentId)
      .filter(
        (rent) =>
          !!rent.rentLines.find((line) =>
            serialNumbers.includes(Number(line.asset.serialNumber)),
          ),
      );

    // Sort the rents  desc by rent end date
    const endRentsDate: string[] = assetRents
      .sort((rentA, rentB) => {
        if (dayjs(rentA.endDate).isAfter(rentB.endDate)) {
          return -1;
        }

        if (dayjs(rentA.endDate).isBefore(rentB.endDate)) {
          return 1;
        }

        return 0;
      })
      .map((rent) => rent.endDate);

    const closestNextEndDate = endRentsDate.find((date) =>
      selectedEndDate.isAfter(date),
    );

    if (!closestNextEndDate) {
      return true;
    }
    return current.isAfter(closestNextEndDate, 'days');
  }

  return {
    ready: !!rentsQuery.data,
    isAvailable,
    isAvailableEndDate: isAvailableEndDate,
    isAvailableStartDate,
  };
}
